import { useEffect, useState } from 'react'

import standBackside from '../assets/stand-backside.avif'
import { cn } from '@/lib/utils'
import type { StampStyle } from '@/lib/useCustomizeDraft'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/select'
import { OptionCard, RadioGroup } from '@/components/ui/radio-group'
import { Dialog, DialogBody, DialogContent } from '@/components/ui/dialog'
import { CloseIcon } from '@/components/Icons'
import { StoreImage } from '@/components/StoreImage'

/**
 * The three hands a stamp can be cut in. `key` is what the order
 * stores and the workshop reads back, so it must stay stable even if
 * the label or the file behind it changes. `css` matches the
 * @font-face families declared in entrypoints/application.css.
 */
export const STAMP_FONTS = [
  { key: 'barcelona', label: 'Barcelona', css: "'StampBarcelona', cursive" },
  { key: 'crysh', label: 'Crysh', css: "'StampCrysh', cursive" },
  { key: 'jamstreet', label: 'Jamstreet', css: "'StampJamstreet', cursive" },
] as const

/** Ink colours offered as one-tap swatches beside the full picker. */
const SWATCHES = ['#FFFFFF', '#0A0A0A', '#EF6CB8', '#22C55E', '#FACC15', '#EF4444']

export const STAMP_SIZE = { min: 24, max: 96 } as const

export const DEFAULT_STAMP_STYLE: StampStyle = {
  font: 'barcelona',
  size: 56,
  color: '#FFFFFF',
}

/** Prefilled so the preview is showing something the moment it opens. */
export const DEFAULT_STAMP_TEXT = 'Test'

export function fontCss(key: string): string {
  return (STAMP_FONTS.find((f) => f.key === key) ?? STAMP_FONTS[0]).css
}

export function fontLabel(key: string): string {
  return (STAMP_FONTS.find((f) => f.key === key) ?? STAMP_FONTS[0]).label
}

/**
 * Live preview — the customer's text laid onto a photo of the stand's
 * back face, in the hand, size and ink they picked.
 *
 * The text is positioned and scaled in percentages of the photo, and
 * the whole overlay is scaled by container width against the width the
 * placement was measured at. That keeps the stamp locked to the same
 * spot on the object whether it's rendering in a 343px popup or a
 * wider desktop one, and makes the px size the customer picks mean the
 * same thing at every viewport.
 */
const PREVIEW_BASE_WIDTH = 420

export function StampPreview({
  text,
  style,
  className,
}: {
  text: string
  style: StampStyle
  className?: string
}) {
  return (
    <div className={cn('relative overflow-hidden bg-[#111]', className)}>
      <StoreImage src={standBackside} alt="" className="block size-full object-cover" />
      {/* Anchored to the flat painted face and tilted to follow it.
          Percentages of the photo, so the stamp stays on the same spot
          on the object at any container width. */}
      <div
        className="pointer-events-none absolute"
        // Centred on the object, not on the frame. Measured from the
        // photo: the stand spans 18%–79.6% of the width, so its middle
        // is 48.8% — the box matches that span rather than the frame's.
        style={{ left: '48.8%', top: '57%', width: '58%', transform: 'translate(-50%, -50%)' }}
      >
        <span
          // Deliberately not truncated: clipping an over-long word eats
          // it from the right only, which drags the visible text off
          // centre. Overflow stays centred and the frame clips it evenly.
          className="block w-full whitespace-nowrap text-center"
          style={{
            fontFamily: fontCss(style.font),
            // Unitless multiplier — `Npx * length` is invalid calc() and
            // silently drops the whole declaration back to inherited.
            fontSize: `calc(${style.size} * (100cqw / ${PREVIEW_BASE_WIDTH}))`,
            color: style.color,
            transform: 'rotate(-2deg)',
            lineHeight: 1.15,
            // Grounds the ink on a photographed surface — without it
            // light text on the pale face reads as a floating label.
            textShadow: '0 2px 12px rgba(0,0,0,0.55)',
          }}
        >
          {text}
        </span>
      </div>
    </div>
  )
}

interface StampStepProps {
  stepKey: string
  title: string
  hint: string | null
  charLimit: number
  placeholder: string | null
  required: boolean
  /** Committed engraving text — empty string when none. */
  value: string
  style: StampStyle
  onChange: (value: string, style: StampStyle) => void
  /** Sentinel the configurator uses for "no engraving". */
  noTextValue: string
}

/**
 * v3 · Stamp — the option pair on the page, the editor in a popup.
 *
 * Choosing "Stamped" opens the popup rather than unfolding a field:
 * the customer is choosing how the piece will look, so the photo has
 * to be the biggest thing on screen. Nothing commits until Save, so
 * dismissing the popup leaves the step as it was.
 */
export function StampStep({
  stepKey,
  title,
  hint,
  charLimit,
  placeholder,
  required,
  value,
  style,
  onChange,
  noTextValue,
}: StampStepProps) {
  const committed = value === noTextValue ? '' : value
  const [open, setOpen] = useState(false)
  const [draftText, setDraftText] = useState(DEFAULT_STAMP_TEXT)
  const [draftStyle, setDraftStyle] = useState<StampStyle>(style)

  // Re-seed the editor each time it opens: from what's already on the
  // piece, or from the default text so the preview is never blank.
  useEffect(() => {
    if (!open) return
    setDraftText(committed || DEFAULT_STAMP_TEXT)
    setDraftStyle(style)
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const save = () => {
    const text = draftText.trim().slice(0, charLimit)
    if (!text) return
    onChange(text, draftStyle)
    setOpen(false)
  }

  const mode = committed ? 'custom' : value === noTextValue ? 'none' : ''

  return (
    <div className="flex flex-col gap-2.5">
      <RadioGroup
        value={mode}
        onValueChange={(next) => {
          if (next === 'none') onChange(noTextValue, style)
          else setOpen(true)
        }}
        className={required ? 'grid-cols-1' : 'grid-cols-2 gap-2.5'}
      >
        {!required && <OptionCard value="none" title="No text" meta="Clean base" />}
        <OptionCard
          value="custom"
          title="Stamped"
          meta={committed ? `“${committed}” · ${fontLabel(style.font)}` : `Up to ${charLimit} characters`}
          onClick={() => {
            if (mode === 'custom') setOpen(true)
          }}
        />
      </RadioGroup>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[420px]">
          {/* The preview takes the slot the title block used to hold —
              seeing the piece beats reading about it. */}
          <div className="relative shrink-0">
            <StampPreview
              text={draftText || DEFAULT_STAMP_TEXT}
              style={draftStyle}
              // Gives up height first on a short screen so the controls
              // below it keep as much room as possible.
              className="h-[210px] max-h-[28dvh] w-full [container-type:inline-size]"
            />
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="absolute right-3.5 top-3.5 flex size-8 cursor-pointer items-center justify-center rounded-full border border-[rgba(255,255,255,0.12)] bg-[rgba(10,10,10,0.75)] text-white"
            >
              <CloseIcon size={16} />
            </button>
          </div>

          <DialogBody>
            <div>
              <div className="text-[20px] font-bold tracking-[-0.02em]">{title}</div>
              {hint && <p className="mt-1.5 text-[14px] leading-relaxed text-ink-muted">{hint}</p>}
            </div>

            <Field
              label="Engraving text"
              htmlFor={`stamp-${stepKey}`}
              counter={`${draftText.length}/${charLimit}`}
              hint="Engraved pieces can’t be returned."
            >
              <Input
                id={`stamp-${stepKey}`}
                value={draftText}
                maxLength={charLimit}
                placeholder={placeholder ?? ''}
                onChange={(e) => setDraftText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    save()
                  }
                }}
              />
            </Field>

            <Field label="Font" htmlFor={`stamp-font-${stepKey}`}>
              <NativeSelect
                id={`stamp-font-${stepKey}`}
                value={draftStyle.font}
                onChange={(e) => setDraftStyle({ ...draftStyle, font: e.target.value })}
              >
                {STAMP_FONTS.map((font) => (
                  <option key={font.key} value={font.key}>
                    {font.label}
                  </option>
                ))}
              </NativeSelect>
            </Field>

            <Field
              label="Size"
              htmlFor={`stamp-size-${stepKey}`}
              counter={`${draftStyle.size}px`}
            >
              <input
                id={`stamp-size-${stepKey}`}
                type="range"
                min={STAMP_SIZE.min}
                max={STAMP_SIZE.max}
                value={draftStyle.size}
                onChange={(e) => setDraftStyle({ ...draftStyle, size: Number(e.target.value) })}
                className="sss-range w-full"
              />
            </Field>

            <Field label="Colour" htmlFor={`stamp-color-${stepKey}`}>
              <div className="flex items-center gap-2.5">
                {/* The swatches scroll rather than overflow the popup —
                    the selected ring needs 4px of clearance, so the
                    strip is padded and the scroller given matching
                    negative margin to keep the row optically flush. */}
                <div className="-my-1 flex min-w-0 flex-1 items-center gap-2.5 overflow-x-auto px-1 py-1">
                  {SWATCHES.map((swatch) => (
                    <button
                      key={swatch}
                      type="button"
                      aria-label={swatch}
                      aria-pressed={draftStyle.color.toUpperCase() === swatch}
                      onClick={() => setDraftStyle({ ...draftStyle, color: swatch })}
                      className={cn(
                        'size-8 shrink-0 cursor-pointer rounded-full border',
                        draftStyle.color.toUpperCase() === swatch
                          ? 'border-ink shadow-[0_0_0_2px_var(--sss-surface),0_0_0_4px_var(--sss-ink)]'
                          : 'border-hairline'
                      )}
                      style={{ background: swatch }}
                    />
                  ))}
                </div>
                {/* The wheel itself — the OS picker, for anything the
                    swatches don't cover. Pinned outside the scroller so
                    it never scrolls out of reach. */}
                <label className="relative size-8 shrink-0 cursor-pointer overflow-hidden rounded-full border border-hairline">
                  <span
                    aria-hidden="true"
                    className="absolute inset-0"
                    style={{
                      background:
                        'conic-gradient(#EF4444,#FACC15,#22C55E,#22D3EE,#3B82F6,#A855F7,#EF4444)',
                    }}
                  />
                  <input
                    id={`stamp-color-${stepKey}`}
                    type="color"
                    value={draftStyle.color}
                    onChange={(e) => setDraftStyle({ ...draftStyle, color: e.target.value })}
                    className="absolute inset-0 cursor-pointer opacity-0"
                  />
                </label>
              </div>
            </Field>

            {/* Same shape as "Continue to payment" — the popup's commit
                action should read identically to the flow's. */}
            <Button variant="primary" size="block" disabled={!draftText.trim()} onClick={save}>
              Save stamp
            </Button>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  )
}
